import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  makeStyles,
  FormControl,
  InputLabel,
  ListItemIcon,
  Typography
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 400,
    },
  },
  formControl: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkIcon: {
    marginLeft: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  menuPaper: {
    maxHeight: 300,
  },
}));

const ChangeCompanyModal = ({
  open,
  onClose,
  currentCompany,
  companies = [],
  onSubmit,
}) => {
  const classes = useStyles();
  const [selectedCompany, setSelectedCompany] = React.useState(currentCompany);
  
  // IMPORTANT: Force reset of selected company to current whenever dialog opens
  React.useEffect(() => {
    if (open) {
      // Using setTimeout to ensure this happens after render
      setTimeout(() => {
        setSelectedCompany(currentCompany);
      }, 0);
    }
  }, [open, currentCompany]);

  const handleChange = (event) => {
    setSelectedCompany(event.target.value);
  };

  const handleSubmit = () => {
    onSubmit(selectedCompany);
  };

  // Find the currently selected company name for display
  const currentCompanyName = React.useMemo(() => {
    const company = companies.find(c => c.companyReference === currentCompany);
    return company ? company.companyName : "";
  }, [companies, currentCompany]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      aria-labelledby="change-company-dialog"
    >
      <DialogTitle id="change-company-dialog">Change Company</DialogTitle>
      <DialogContent>
        <FormControl variant="outlined" className={classes.formControl}>
          {/* Remove the InputLabel to avoid placeholder text conflicts */}
          <Select
            labelId="company-select-label"
            id="company-select"
            value={selectedCompany || ""}
            onChange={handleChange}
            fullWidth
            displayEmpty
            defaultValue={currentCompany}
            MenuProps={{
              PaperProps: { className: classes.menuPaper },
              getContentAnchorEl: null,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              }
            }}
            renderValue={(selected) => {
              // First try to get company from selected value
              const selectedCompanyObj = companies.find(c => c.companyReference === selected);
              
              // If not found, get current company
              if (!selectedCompanyObj) {
                const currentCompanyObj = companies.find(c => c.companyReference === currentCompany);
                return currentCompanyObj ? currentCompanyObj.companyName : "Select Company";
              }
              
              return selectedCompanyObj.companyName;
            }}
          >
            {companies.length > 0 ? (
              companies.map((company) => (
                <MenuItem 
                  key={company.companyReference} 
                  value={company.companyReference}
                  className={classes.menuItem}
                >
                  <Typography>
                    {company.companyName}
                  </Typography>
                  {company.companyReference === currentCompany && (
                    <ListItemIcon className={classes.checkIcon}>
                      <CheckIcon fontSize="small" />
                    </ListItemIcon>
                  )}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                No companies available
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={!selectedCompany || selectedCompany === currentCompany}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeCompanyModal;